import React, { useEffect, useState, useRef } from 'react';
import { Box, Container, Spinner, Text, useToast } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useMutation } from '@tanstack/react-query';

import LexicalArticleViewer from '../../components/Lexical/LexicalArticleViewer';
import ArticleHeader from './components/ArticleHeader';
import GeneralComment from './components/GeneralComment';
import RelatedContentContainer from './components/ArticleFooter';
import ArticleOpinion from './components/ArticleOpinion';

import { useArticleQuery } from '../../apis/queries/useGetArticleQuery';
import { viewArticle } from '../../apis/article.api';
import ToastBtnMessage from 'src/components/ToastBtnMessage';
import SEO from '../../components/SEO';
import { generateSEOMetadata } from '../../utils/seo';
import DataLayer from '../../utils/data-layer';

const ArticleViewer = () => {
  const { articleId } = useParams();
  const toast = useToast();
  const token = Cookies.get('token');

  const [showSensitiveContentCard, setShowSensitiveContentCard] = useState(false);
  const trackedArticleIdRef = useRef<string | undefined>(undefined);

  const { isLoading, error, data: article, refetch } = useArticleQuery(articleId!);

  const { mutateAsync: viewArticleAsync } = useMutation({ mutationFn: viewArticle });

  useEffect(() => {
    window.scrollTo(0, 0);
    setShowSensitiveContentCard(false);
    // Reset tracking ref when articleId changes (new article)
    trackedArticleIdRef.current = undefined;
  }, [articleId]);

  useEffect(() => {
    if (!token || !articleId) return;
    viewArticleAsync(articleId).catch((err) => console.error('Error updating view count:', err));
  }, [articleId, token]);

  useEffect(() => {
    if (article?.moderation?.showWarning !== undefined) {
      setShowSensitiveContentCard(article.moderation.showWarning);
    }
  }, [article]);

  useEffect(() => {
    // Only track once per articleId, not on refetches
    if (article && articleId && articleId !== trackedArticleIdRef.current) {
      trackedArticleIdRef.current = articleId;
      const articleType = article.writingType?.name;
      DataLayer.trackArticleView(articleType, articleId);
    }
  }, [article, articleId]);

  if (isLoading) {
    return (
      <Box height="calc(100vh - 64px)" display="flex" alignItems="center" justifyContent="center">
        <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="green.500" size="xl" />
      </Box>
    );
  }

  if (error || !article) {
    toast({
      render: () => (
        <ToastBtnMessage title="Couldn't load article" isError color="jet-black" isHiddenButton />
      ),
      status: 'error',
      duration: 2000,
      isClosable: true,
      position: 'top-right'
    });

    return (
      <Box
        height="calc(100vh - 64px)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
        textAlign="center"
      >
        <Text fontSize="lg" color="gray.600">
          Article not found or has been removed.
        </Text>
      </Box>
    );
  }

  // Generate SEO metadata for article page
  const seoMetadata = generateSEOMetadata.article(article);

  return (
    <div className="relative">
      <SEO {...seoMetadata} />
      <Container maxW="812px" className="!px-4 md:!px-0" position="relative">
        <ArticleHeader
          showSensitiveContentCard={showSensitiveContentCard}
          setShowSensitiveContentCard={setShowSensitiveContentCard}
          article={article}
          refetch={refetch}
          isLoading={isLoading}
        />
        <LexicalArticleViewer
          key={articleId} // Force re-render
          showSensitiveContentCard={showSensitiveContentCard}
          content={article.content}
          articleId={article.id}
          article={article}
        />
        <Box height="1px" bg="gray.200" mx="auto" my={8} maxW="400px" opacity={0.6} />
        <ArticleOpinion
          articleId={article.id}
          showSensitiveContentCard={showSensitiveContentCard}
          isShowSensitive={article.isShowSensitive}
          article={article}
        />

        <GeneralComment
          showSensitiveContentCard={showSensitiveContentCard}
          article={article}
          isShowSensitive={article.isShowSensitive}
          commentCount={article.totalGeneralComments}
        />
      </Container>
      <RelatedContentContainer articleId={article.id} />
    </div>
  );
};

export default ArticleViewer;
